import { A, useLocation, useNavigate } from "@solidjs/router";
import { createChatClient } from "@storytell/client";
import { Logo, LogoNoText, type PageSlug } from "@storytell/core";
import { dispatchGlobalEvent, subscribeToGlobalEvent } from "@storytell/events";
import type { StreamingChatId } from "@storytell/streaming";
import { StreamingChatStatus } from "@storytell/streaming";
import type { ProjectId } from "@storytell/types";
import { StButton } from "@storytell/ui/buttons";
import { StDropdown, StDropdownItem, StDropdownTrigger } from "@storytell/ui/dropdown";
import { StTooltip, StTooltipSuppressionProvider, StTooltipTrigger } from "@storytell/ui/tooltip";
import type { IconTypes } from "solid-icons";
import { FaSolidGraduationCap } from "solid-icons/fa";
import { RiUserFacesUserStarLine } from "solid-icons/ri";
import {
  TbApps,
  TbBolt,
  TbCheck,
  TbChevronDown,
  TbFilter,
  TbHelp,
  TbHistory,
  TbLayoutSidebar,
  TbListCheck,
  TbLoader2,
  TbPlus,
  TbRefresh,
  TbSearch,
  TbSettings,
  TbStack,
  TbTag,
  TbWaveSquare,
} from "solid-icons/tb";
import { createMemo, createSignal, For, Match, onCleanup, onMount, Show, Suspense, Switch } from "solid-js";
import { isServer, Portal } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { Avatar } from "~/components/avatar/Avatar";
import { DeleteChatModal } from "~/components/chat/DeleteChatModal";
import { MoveChatModal } from "~/components/chat/MoveChatModal";
import { ShareChatModal } from "~/components/chat/ShareChatModal";
import { UserAccountPopover } from "~/components/common/UserAccountPopover";
import { LuLibrary } from "~/components/icons/LuLibrary";
import { isSettingsPath, SettingsNavCollapsed, SettingsNavExpanded } from "~/components/layouts/SettingsShell";
import { ProjectsNavSectionExpanded } from "~/components/navigation/ProjectsNavSectionExpanded";
import { ThemeToggle } from "~/components/navigation/ThemeToggle";
import { WhatsNewPopover } from "~/components/navigation/WhatsNewPopover";
import { NotificationBell } from "~/components/notifications/NotificationBell";
import { OrganizationSwitcherModal } from "~/components/organization/OrganizationSwitcherModal";
import { guardUnsavedProjectDetails } from "~/components/projects/projectDetailsEditGuard";
import { ProjectSwitcherModal } from "~/components/projects/ProjectSwitcherModal";
import { KeyboardShortcutInlinePill } from "~/components/ui/KeyboardShortcutInlinePill";
import { authGateStatus } from "~/config/authGating";
import { SUPPORT_MAILTO } from "~/constants/urls";
import { useChatsAuthorFilter } from "~/hooks/useChatsAuthorFilter";
import { useGateActionByAuthenticated } from "~/hooks/useGateActionByAuthenticated";
import { useKeyboardShortcutSuggestions } from "~/hooks/useKeyboardShortcutSuggestions";
import { useUserMode } from "~/hooks/useUserMode";
import { useI18n } from "~/i18n";
import type { TranslationKey } from "~/i18n/translations";
import { extractErrorInfo } from "~/lib/error-handling";
import { consumePendingHighlight } from "~/lib/highlightAdvancedModePending";
import { trackFeature } from "~/lib/trackFeature";
import {
  type ChatDateGroup,
  formatChatTimeOfDay,
  formatChatTimestampDate,
  getChatAuditLines,
  getChatDateGroup,
  getDisplayTimestamp,
  isChatMine,
  lastActivityMs,
} from "~/utils/chatListAudit";
import { prefetchChatOnIntent } from "~/utils/prefetchChatOnIntent";
import { useWire } from "~/wire";

const [updateAvailable, setUpdateAvailable] = createSignal(false);
if (!isServer) {
  subscribeToGlobalEvent("sw-update-available", () => {
    setUpdateAvailable(true);
  });
}

// Persist the in-flight prompt draft before the manual update-reload, since this
// button is the fallback shown when entry-client defers its own reload.
const reloadForUpdate = () => {
  dispatchGlobalEvent("flush-prompt-bar-draft", undefined);
  window.location.reload();
};

function clearPromptDraft(projectId: string | undefined): void {
  const draftKey = projectId ? `prompt-draft:v2:${projectId}` : "prompt-draft:v2";
  try {
    localStorage.removeItem(draftKey);
  } catch {
    // ignore
  }
}

function runStartNewTask(wire: ReturnType<typeof useWire>): void {
  clearPromptDraft(wire.userContext.get()?.projectId);
  dispatchGlobalEvent("clear-prompt-bar", undefined);
  wire.navigation.navigateHome();
}

const RECENT_CHAT_GROUPS = [
  { id: "today", labelKey: "nav_recent_today" },
  { id: "yesterday", labelKey: "nav_recent_yesterday" },
  { id: "previous7", labelKey: "nav_recent_previous_7_days" },
  { id: "older", labelKey: "nav_recent_older" },
] as const satisfies ReadonlyArray<{ id: ChatDateGroup; labelKey: TranslationKey }>;

/** Section rank, shared with the project switcher so the sidebar has one treatment per rank. */
const SIDEBAR_GROUP_LABEL = "px-4 text-xs font-semibold uppercase tracking-wide text-secondary-text";

/** Subdivision rank — sentence case and muted, so it never competes with the section above it. */
const RECENT_DATE_LABEL = "px-4 pt-2 pb-0.5 text-xs font-medium text-muted-text";

/** Buckets newest-first within each local day; empty buckets drop out. */
function groupRecentChats<T extends Parameters<typeof getDisplayTimestamp>[0] & Parameters<typeof lastActivityMs>[0]>(
  chats: readonly T[]
) {
  const buckets = new Map<ChatDateGroup, T[]>();
  for (const chat of chats) {
    const timestamp = getDisplayTimestamp(chat);
    const id = timestamp ? getChatDateGroup(timestamp) : "older";
    const bucket = buckets.get(id);
    if (bucket) bucket.push(chat);
    else buckets.set(id, [chat]);
  }
  return RECENT_CHAT_GROUPS.map((group) => ({
    ...group,
    chats: (buckets.get(group.id) ?? []).toSorted((a, b) => lastActivityMs(b) - lastActivityMs(a)),
  })).filter((group) => group.chats.length > 0);
}

/** Under a date heading the day is already known, so today/yesterday rows show only a clock time. */
function recentChatTimestamp(group: ChatDateGroup, timestamp: Parameters<typeof formatChatTimestampDate>[0]): string {
  return group === "today" || group === "yesterday"
    ? formatChatTimeOfDay(timestamp)
    : formatChatTimestampDate(timestamp);
}

async function doDeleteChat(chat: { chatId: string }, wire: ReturnType<typeof useWire>): Promise<void> {
  const chatClient = createChatClient(wire.auth.token());
  await chatClient.deleteChat(chat.chatId);
  trackFeature({ feature: "chat.delete", via: "menu" });
  dispatchGlobalEvent("chat-deleted", { chatId: chat.chatId as `chat_${string}` });
  if (wire.navigation.state().primaryId === chat.chatId) {
    clearPromptDraft(wire.userContext.get()?.projectId);
    dispatchGlobalEvent("clear-prompt-bar", undefined);
    wire.navigation.navigateHome();
  }
}

/** First letters of the project's first two words, for the collapsed rail's project chip. */
function projectInitials(name: string | undefined): string {
  if (!name) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("");
}

/** Short URL path for a page slug (e.g. /chats, /files). Used for nav links so URLs stay as shortlinks. */
function shortLinkForPage(page: PageSlug): string {
  return page === "assets" ? "/files" : `/${page}`;
}

/**
 * Click handler for the project-scoped page nav links. A logged-out guest on a
 * project-less route has no project to open the page
 * in, so navigateToPage silently no-ops and the whole navbar looks dead
 * (ENG-6244). In that case prompt the guest to sign in instead of swallowing
 * the click; signed-in users and guests already inside a project navigate as
 * before. While auth is still resolving the click is dropped rather than
 * prompted: nobody knows yet whether this is a guest, and telling a signed-in
 * user to sign up is the worse of the two. On the scope-less /upgrade route a
 * signed-in user has no project in the URL or active context, so recover it
 * from recents (ENG-6979).
 */
const useNavPageClick = () => {
  const wire = useWire();
  const nav = wire.navigation;
  const layout = wire.ui.layout;
  const { t } = useI18n();

  return (page: PageSlug, e: MouseEvent): void => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    // Recents survive sign-out in localStorage, so only a registered user may
    // recover a project from them — otherwise the sign-in prompt below never fires.
    const gate = authGateStatus(wire.auth, null);
    const recentProjectId = gate === "allow" ? wire.userContext.getRecentProjectIds()[0] : undefined;
    const projectId = nav.state().projectId ?? wire.currentProjectId() ?? recentProjectId;
    if (!projectId) {
      if (gate === "gated") {
        wire.ui.authGate.openModal({ title: t("modal_signup_create_skill_title"), feature: "navigation" });
      }
      return;
    }
    nav.navigateToPage(page, { projectId });
    if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
  };
};

interface NavLinkProps {
  page: PageSlug;
  icon: IconTypes;
  label: string;
  /** When true, show cyan glow (e.g. after switching to Advanced). Pass accessor so it updates reactively. */
  highlight?: boolean | (() => boolean);
  /** Sub-item of the row above: indented to that row's label axis and muted, with the fill still full width. */
  nested?: boolean;
  /** A path under the page's route, opened as a plain link; `active` then says when it is current. */
  href?: string;
  active?: boolean;
}

/** Navigation link that uses StButton with A component for proper link behavior */
const NavLink = (props: NavLinkProps) => {
  const wire = useWire();
  const nav = wire.navigation;
  const navClick = useNavPageClick();
  const highlight = () => (typeof props.highlight === "function" ? props.highlight() : !!props.highlight);

  const isActive = () => props.active ?? nav.state().primaryId === props.page;

  return (
    <div classList={{ "ring-2 ring-cyan-400/70 bg-cyan-500/15 rounded-md": highlight() }}>
      <StButton
        as={A}
        href={props.href ?? shortLinkForPage(props.page)}
        onClick={(e: MouseEvent) => {
          if (!props.href || !(nav.state().projectId ?? wire.currentProjectId())) return navClick(props.page, e);
          if (wire.ui.layout.isSmallScreen()) wire.ui.layout.setNavbarCollapsed(true);
        }}
        icon={props.icon}
        noBorder
        noColor
        class={twMerge(
          "w-full justify-start py-2 text-sm whitespace-nowrap transition-colors [&_svg]:size-4",
          // 36px lands the sub-item's icon on the parent row's label, so the tier
          // reads from the text column rather than from a rule down the side.
          props.nested ? "pl-9" : "pl-4"
        )}
        classList={{
          "bg-panel-hover text-primary-text font-medium": isActive(),
          "hover:bg-canvas": !isActive(),
          "text-secondary-text": props.nested && !isActive(),
        }}
      >
        {props.label}
      </StButton>
    </div>
  );
};

interface NavLinkIconProps {
  page: PageSlug;
  icon: IconTypes;
  tooltip: string;
  /** When true, show cyan glow (e.g. after switching to Advanced user mode). Pass accessor so it updates reactively. */
  highlight?: boolean | (() => boolean);
}

/** Icon-only navigation link for collapsed navbar */
const NavLinkIcon = (props: NavLinkIconProps) => {
  const wire = useWire();
  const nav = wire.navigation;
  const navClick = useNavPageClick();
  const highlight = () => (typeof props.highlight === "function" ? props.highlight() : !!props.highlight);

  const isActive = () => nav.state().primaryId === props.page;

  return (
    <StTooltip content={props.tooltip} placement="right">
      <StTooltipTrigger as="div" classList={{ "ring-2 ring-cyan-400/70 bg-cyan-500/15 rounded-md": highlight() }}>
        <StButton
          as={A}
          href={shortLinkForPage(props.page)}
          onClick={(e: MouseEvent) => navClick(props.page, e)}
          icon={props.icon}
          noBorder
          noColor
          aria-label={props.tooltip}
          class="w-full justify-start py-1 pl-2 transition-colors [&_svg]:size-4"
          classList={{
            "bg-panel-hover text-primary-text font-medium": isActive(),
            "hover:bg-canvas": !isActive(),
          }}
        />
      </StTooltipTrigger>
    </StTooltip>
  );
};

const useRailFlyout = () => {
  const [open, setOpen] = createSignal(false);
  const [anchor, setAnchor] = createSignal<DOMRect | null>(null);
  let triggerRef: HTMLDivElement | undefined;
  let panelRef: HTMLDivElement | undefined;

  const close = () => setOpen(false);

  const toggle = () => {
    if (open()) {
      close();
      return;
    }
    if (triggerRef) setAnchor(triggerRef.getBoundingClientRect());
    setOpen(true);
  };

  onMount(() => {
    const handlePointerDown = (e: PointerEvent) => {
      if (!open()) return;
      const target = e.target as Node | null;
      if (!target) return;
      // The panel is portalled, so it is not a descendant of the trigger.
      if (triggerRef?.contains(target) || panelRef?.contains(target)) return;
      close();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    onCleanup(() => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    });
  });

  const panelStyle = () => {
    const rect = anchor();
    if (!rect) return undefined;
    return {
      top: `${rect.top}px`,
      left: `${rect.right}px`,
      "max-height": `calc(100vh - ${rect.top}px - 0.5rem)`,
    };
  };

  return {
    open,
    toggle,
    close,
    panelStyle,
    setTrigger: (el: HTMLDivElement) => {
      triggerRef = el;
    },
    setPanel: (el: HTMLDivElement) => {
      panelRef = el;
    },
  };
};

// Fixed and portalled to the body: the rail's <aside> is overflow-hidden at 48px,
// which clips an absolutely-positioned child to a few pixels no matter its z-index.
// z-[70] clears the side panel (z-40) and the drawer backdrop (z-30).
const RAIL_FLYOUT =
  "fixed z-[70] min-w-[13rem] flex-col gap-0.5 overflow-y-auto rounded-lg border border-primary-border bg-panel py-1 px-1 shadow-lg";

function isLibraryNavSubtreePage(id: string | undefined): boolean {
  return id === "assets" || id === "labels" || id === "collections";
}

/** Library row with Labels (+ Collections when advanced) as a disclosure */
const LibraryNavSectionExpanded = () => {
  const wire = useWire();
  const nav = wire.navigation;
  const { t } = useI18n();
  const { isAdvanced: isUserModeAdvanced } = useUserMode();

  const libraryBranchActive = createMemo(() => isLibraryNavSubtreePage(nav.state().primaryId));

  // Being inside the branch opens it; the chevron then overrides that either way.
  // Hover used to be the only opener, which shifted every row below it and left
  // the sub-items unreachable on touch.
  const [openOverride, setOpenOverride] = createSignal<boolean | undefined>();
  const expanded = () => openOverride() ?? libraryBranchActive();

  return (
    <div class="w-full">
      {/* The chevron sits over the row rather than inside it: the row is a link, and a
          button nested in one is invalid and swallows the navigation click. */}
      <div class="relative">
        <NavLink page="assets" icon={LuLibrary} label={t("project_homepage.library")} />
        <button
          type="button"
          onClick={() => setOpenOverride(!expanded())}
          aria-expanded={expanded()}
          aria-controls="library-subnav"
          aria-label={t("project_homepage.library")}
          class="absolute top-1/2 right-2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-text transition-colors hover:bg-canvas-hover hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          <TbChevronDown class="size-3.5 transition-transform" classList={{ "rotate-180": expanded() }} aria-hidden />
        </button>
      </div>
      <div
        id="library-subnav"
        class="grid transition-[grid-template-rows] duration-200 ease-out"
        classList={{ "grid-rows-[1fr]": expanded(), "grid-rows-[0fr]": !expanded() }}
      >
        <div class="overflow-hidden">
          <NavLink nested page="labels" icon={TbTag} label={t("entity.labels")} />
          <Show when={isUserModeAdvanced()}>
            <NavLink nested page="collections" icon={TbStack} label={t("entity.collections")} />
          </Show>
        </div>
      </div>
    </div>
  );
};

/** Live Sessions row with its Tags page as a disclosure, the way Library carries Labels */
const LiveSessionsNavSectionExpanded = () => {
  const wire = useWire();
  const nav = wire.navigation;
  const location = useLocation();
  const { t } = useI18n();

  const onTags = () => /\/live-sessions\/tags\/?$/.test(location.pathname);
  const [openOverride, setOpenOverride] = createSignal<boolean | undefined>();
  const expanded = () => openOverride() ?? nav.state().primaryId === "live-sessions";

  return (
    <div class="w-full">
      <div class="relative">
        <NavLink
          page="live-sessions"
          icon={TbWaveSquare}
          label={t("nav_live_sessions")}
          active={nav.state().primaryId === "live-sessions" && !onTags()}
        />
        <button
          type="button"
          onClick={() => setOpenOverride(!expanded())}
          aria-expanded={expanded()}
          aria-controls="live-sessions-subnav"
          aria-label={t("nav_live_sessions")}
          class="absolute top-1/2 right-2 flex size-6 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-muted-text transition-colors hover:bg-canvas-hover hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
        >
          <TbChevronDown class="size-3.5 transition-transform" classList={{ "rotate-180": expanded() }} aria-hidden />
        </button>
      </div>
      <div
        id="live-sessions-subnav"
        class="grid transition-[grid-template-rows] duration-200 ease-out"
        classList={{ "grid-rows-[1fr]": expanded(), "grid-rows-[0fr]": !expanded() }}
      >
        <div class="overflow-hidden">
          <NavLink
            nested
            page="live-sessions"
            href={`/project/${nav.state().projectId ?? wire.currentProjectId() ?? ""}/live-sessions/tags`}
            active={onTags()}
            icon={TbTag}
            label={t("live_sessions.tags.title")}
          />
        </div>
      </div>
    </div>
  );
};

/** Recent chats reach the rail through a flyout, since the list can't render at 48px. */
const RecentChatsNavSectionCollapsed = () => {
  const wire = useWire();
  const nav = wire.navigation;
  const layout = wire.ui.layout;
  const { t } = useI18n();
  const flyout = useRailFlyout();
  const { authorFilter } = useChatsAuthorFilter();

  // Same persisted signal the expanded list writes, so the rail never shows a
  // different scope than the sidebar the user last set.
  const chats = createMemo(() => {
    const list = wire.recentChats.chats() ?? [];
    const me = wire.auth.user()?.id;
    const myEmail = wire.auth.user()?.primaryEmail;
    if (authorFilter() === "mine" && (me || myEmail)) return list.filter((c) => isChatMine(c, me, myEmail));
    return list;
  });

  const openChat = (chatId: string) => {
    flyout.close();
    nav.navigateToPrimary(chatId, { preserveSidePanel: true });
    if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
  };

  return (
    <div ref={flyout.setTrigger} class="w-full">
      <StTooltip content={t("nav_recent_chats")} placement="right">
        <StTooltipTrigger as="div" class="w-full">
          <StButton
            onClick={flyout.toggle}
            icon={TbHistory}
            noBorder
            noColor
            aria-haspopup="menu"
            aria-expanded={flyout.open()}
            aria-label={t("nav_recent_chats")}
            class="w-full justify-start py-1 pl-2 transition-colors hover:bg-canvas [&_svg]:size-4"
          />
        </StTooltipTrigger>
      </StTooltip>
      <Show when={flyout.open()}>
        <Portal>
          <div
            ref={flyout.setPanel}
            class={`${RAIL_FLYOUT} flex`}
            style={flyout.panelStyle()}
            role="group"
            aria-label={t("nav_recent_chats")}
          >
            <Show
              when={chats().length > 0}
              fallback={<span class="px-2 py-1.5 text-sm text-muted-text">{t("nav_recent_chats_empty")}</span>}
            >
              <For each={groupRecentChats(chats())}>
                {(group) => (
                  <>
                    <h3 class={twMerge(RECENT_DATE_LABEL, "px-2")}>{t(group.labelKey)}</h3>
                    <For each={group.chats}>
                      {(item) => (
                        <button
                          type="button"
                          onClick={() => openChat(item.chatId)}
                          {...prefetchChatOnIntent(item.chatId, wire.chat.prefetchChats)}
                          class="flex w-full cursor-pointer items-center rounded-md px-2 py-1.5 text-left text-sm transition-colors"
                          classList={{
                            "bg-panel-hover text-primary-text font-medium": nav.state().primaryId === item.chatId,
                            "hover:bg-canvas": nav.state().primaryId !== item.chatId,
                          }}
                          title={item.title}
                        >
                          <span class="min-w-0 flex-1 truncate">{item.title || t("nav_new_chat")}</span>
                        </button>
                      )}
                    </For>
                  </>
                )}
              </For>
              <Show when={wire.recentChats.hitCap()}>
                <A
                  href="/chats"
                  onClick={flyout.close}
                  class="block rounded-md px-2 py-1.5 text-sm text-muted-text transition-colors hover:bg-canvas hover:text-primary-text"
                >
                  {t("nav_recent_chats_more")}
                </A>
              </Show>
            </Show>
          </div>
        </Portal>
      </Show>
    </div>
  );
};

const LibraryNavSectionCollapsed = (props: { collectionsHighlight: () => boolean }) => {
  const wire = useWire();
  const nav = wire.navigation;
  const navClick = useNavPageClick();
  const { t } = useI18n();
  const { isAdvanced: isUserModeAdvanced } = useUserMode();

  const flyout = useRailFlyout();
  const libraryBranchActive = createMemo(() => isLibraryNavSubtreePage(nav.state().primaryId));

  const makeNavigate =
    (page: PageSlug) =>
    (e: MouseEvent): void => {
      flyout.close();
      navClick(page, e);
    };

  const isActive = (page: PageSlug) => () => nav.state().primaryId === page;

  return (
    <div ref={flyout.setTrigger} class="w-full">
      <StTooltip content={t("project_homepage.library")} placement="right">
        <StTooltipTrigger as="div" class="w-full">
          <StButton
            onClick={flyout.toggle}
            icon={LuLibrary}
            noBorder
            noColor
            aria-haspopup="menu"
            aria-expanded={flyout.open()}
            aria-label={t("project_homepage.library")}
            class="w-full justify-start py-1 pl-2 transition-colors [&_svg]:size-4"
            classList={{
              "bg-panel-hover text-primary-text font-medium": libraryBranchActive(),
              "hover:bg-canvas": !libraryBranchActive(),
            }}
          />
        </StTooltipTrigger>
      </StTooltip>
      <Show when={flyout.open()}>
        <Portal>
          <div
            ref={flyout.setPanel}
            class={`${RAIL_FLYOUT} flex`}
            style={flyout.panelStyle()}
            role="group"
            aria-label={t("project_homepage.library")}
          >
            <A
              href={shortLinkForPage("assets")}
              onClick={makeNavigate("assets")}
              class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors"
              classList={{
                "bg-panel-hover text-primary-text font-medium": isActive("assets")(),
                "hover:bg-canvas": !isActive("assets")(),
              }}
            >
              <LuLibrary class="size-4 shrink-0" aria-hidden />
              {t("project_homepage.library")}
            </A>
            <A
              href={shortLinkForPage("labels")}
              onClick={makeNavigate("labels")}
              class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors"
              classList={{
                "bg-panel-hover text-primary-text font-medium": isActive("labels")(),
                "hover:bg-canvas": !isActive("labels")(),
              }}
            >
              <TbTag class="size-4 shrink-0" aria-hidden />
              {t("entity.labels")}
            </A>
            <Show when={isUserModeAdvanced()}>
              <div classList={{ "ring-2 ring-cyan-400/70 bg-cyan-500/15 rounded-md": props.collectionsHighlight() }}>
                <A
                  href={shortLinkForPage("collections")}
                  onClick={makeNavigate("collections")}
                  class="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors"
                  classList={{
                    "bg-panel-hover text-primary-text font-medium": isActive("collections")(),
                    "hover:bg-canvas": !isActive("collections")(),
                  }}
                >
                  <TbStack class="size-4 shrink-0" aria-hidden />
                  {t("entity.collections")}
                </A>
              </div>
            </Show>
          </div>
        </Portal>
      </Show>
    </div>
  );
};

// Plain function, not createMemo: a memo created mid-router-transition reads
// back as undefined until the transition commits, and <A>/navigate() crash on
// an undefined href during resolvePath. A function evaluates inline and always
// yields a string. See ENG-6325 (and the same class of bug in ENG-6233).
const useSearchHref = () => {
  const wire = useWire();
  return () => {
    const projectId = wire.currentProjectId();
    return projectId ? `/project/${projectId}/search` : "/search";
  };
};

const useHomeHref = () => {
  const wire = useWire();
  return () => {
    const projectId = wire.currentProjectId();
    if (projectId) return `/project/${projectId}`;
    const orgId = wire.homeOrgId();
    return orgId ? `/orgs/${orgId}` : "/";
  };
};

export const NavBar = () => {
  const wire = useWire();
  const location = useLocation();
  const { shortcutsActive } = useKeyboardShortcutSuggestions();

  // -- Keyboard shortcut: Opt+Shift+N for new chat (always active regardless of nav state) --
  onMount(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!shortcutsActive()) return;
      if (e.altKey && e.shiftKey && e.code === "KeyN") {
        e.preventDefault();
        e.stopPropagation();
        trackFeature({ feature: "nav.shortcut", action: "opt_shift_n", via: "shortcut" });
        const startNewTask = () => {
          runStartNewTask(wire);
          if (wire.ui.layout.isSmallScreen()) wire.ui.layout.setNavbarCollapsed(true);
        };
        if (guardUnsavedProjectDetails(startNewTask)) return;
        startNewTask();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    onCleanup(() => document.removeEventListener("keydown", onKeyDown, true));
  });

  const settingsMode = () => isSettingsPath(location.pathname);

  return (
    <Show
      when={wire.ui.layout.navbarCollapsed()}
      fallback={
        // The container's padding is where row fills stop; each row's own px-4 sets
        // the column's alignment axis 24px in, level with the logo mark. The rail
        // below keeps the same padding but centres, since 48px has no axis to hold.
        <div class="flex h-full min-h-0 flex-col space-y-2 p-2">
          <SidebarHeaderExpanded />
          <Show when={settingsMode()} fallback={<MainNavExpanded />}>
            <SettingsNavExpanded />
          </Show>
          <SidebarFooterExpanded />
        </div>
      }
    >
      <StTooltipSuppressionProvider suppressed={() => false}>
        <div class="flex h-full flex-col space-y-2 p-2">
          <SidebarHeaderCollapsed />
          <Show when={settingsMode()} fallback={<MainNavCollapsed />}>
            <SettingsNavCollapsed />
          </Show>
          <SidebarFooterCollapsed />
        </div>
      </StTooltipSuppressionProvider>
    </Show>
  );
};

/** Logo row and the pin/collapse control, shared by both sidebar content modes. */
const SidebarHeaderExpanded = () => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const navigate = useNavigate();
  const { t } = useI18n();
  const homeHref = useHomeHref();
  const searchHref = useSearchHref();

  const handleLogoClick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    navigate(homeHref());
  };

  return (
    // Height is stated rather than inherited from the logo link's padding, so the
    // logo and the buttons both centre against the row instead of against each other.
    <div class="flex h-9 items-center justify-between pr-2.5">
      {/* Inset comes from the link, not a margin on the logo, so both modes land on the same spot. */}
      <A href={homeHref()} onClick={handleLogoClick} class="px-4">
        <Logo class="h-5 w-auto" markClass="text-accent" textClass="text-primary-text" />
      </A>

      <div class="flex items-center">
        <StTooltip content={t("chats.search.input.placeholder")} placement="bottom">
          <StTooltipTrigger as="div">
            <StButton
              as={A}
              href={searchHref()}
              onClick={() => {
                if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
              }}
              icon={TbSearch}
              noBorder
              noColor
              aria-label={t("chats.search.input.aria_label")}
              class="hover:bg-canvas"
            />
          </StTooltipTrigger>
        </StTooltip>

        <StTooltip content={t("nav_close_panel")} placement="bottom">
          <StTooltipTrigger as="div">
            <StButton
              onClick={() => {
                trackFeature({ feature: "nav.sidebar_toggle", via: "button" });
                layout.setNavbarCollapsed(true);
              }}
              icon={TbLayoutSidebar}
              noBorder
              noColor
              class="hover:bg-canvas"
            />
          </StTooltipTrigger>
        </StTooltip>
      </div>
    </div>
  );
};

const SidebarHeaderCollapsed = () => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const navigate = useNavigate();
  const { t } = useI18n();
  const homeHref = useHomeHref();

  const handleLogoClick = (e: MouseEvent) => {
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    navigate(homeHref());
  };

  return (
    <>
      <StTooltip content={t("nav_home")} placement="right">
        <StTooltipTrigger as="div" class="w-full">
          <A href={homeHref()} onClick={handleLogoClick} aria-label={t("nav_home")}>
            <LogoNoText class="ml-0.5 size-7 text-accent" />
          </A>
        </StTooltipTrigger>
      </StTooltip>
      <StTooltip content={t("nav_open_menu")} placement="right">
        <StTooltipTrigger as="div" class="w-full">
          <StButton
            onClick={() => {
              trackFeature({ feature: "nav.sidebar_toggle", via: "button" });
              layout.setNavbarCollapsed(false);
            }}
            icon={TbLayoutSidebar}
            noBorder
            noColor
            aria-label={t("nav_open_menu")}
            class="w-full hover:bg-canvas"
          />
        </StTooltipTrigger>
      </StTooltip>
    </>
  );
};

/**
 * Author filter for the recent-tasks list. Writes the same module-level persisted
 * signal the tasks page reads, so the two surfaces never disagree.
 */
const RecentChatsFilterMenu = (props: { value: () => "mine" | "all"; onChange: (value: "mine" | "all") => void }) => {
  const { t } = useI18n();
  const options: ReadonlyArray<{ id: "all" | "mine"; labelKey: TranslationKey }> = [
    { id: "all", labelKey: "common.all" },
    { id: "mine", labelKey: "common.mine" },
  ];

  return (
    <StDropdown
      placement="bottom-end"
      gutter={4}
      contentProps={{ class: "z-50" }}
      content={
        <>
          <span class="block px-2 pb-1 text-xs font-medium text-muted-text">{t("nav_recent_filter_by")}</span>
          <For each={options}>
            {(option) => (
              <StDropdownItem
                role="menuitemradio"
                aria-checked={props.value() === option.id}
                onSelect={() => props.onChange(option.id)}
                class="flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-primary-text transition-colors hover:bg-canvas-hover"
              >
                <TbCheck class="size-4 shrink-0" classList={{ invisible: props.value() !== option.id }} aria-hidden />
                {t(option.labelKey)}
              </StDropdownItem>
            )}
          </For>
        </>
      }
    >
      <StDropdownTrigger
        as="button"
        type="button"
        aria-label={t("nav_recent_filter_aria")}
        class="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-md text-muted-text transition-colors hover:bg-canvas hover:text-primary-text focus-visible:ring-2 focus-visible:ring-brand focus-visible:outline-none"
      >
        <TbFilter class="size-4" aria-hidden />
      </StDropdownTrigger>
    </StDropdown>
  );
};

const MainNavExpanded = () => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const nav = wire.navigation;
  const { t } = useI18n();
  const { isAdvanced: isUserModeAdvanced } = useUserMode();
  const homeHref = useHomeHref();

  const { authorFilter, setAuthorFilter } = useChatsAuthorFilter();
  const [deletingChat, setDeletingChat] = createSignal<{ chatId: string; title: string } | null>(null);
  const [movingChatIds, setMovingChatIds] = createSignal<StreamingChatId[]>([]);
  const [deletingChatError, setDeletingChatError] = createSignal<string | null>(null);
  const [isDeletingChat, setIsDeletingChat] = createSignal(false);
  const closeDeleteChatModal = () => {
    setDeletingChat(null);
    setDeletingChatError(null);
    setIsDeletingChat(false);
  };
  const [sharingChat, setSharingChat] = createSignal<{
    chatId: string;
    title: string;
    textFragment?: string;
    scrollToText?: string;
    scrollToMessageId?: string;
    textBlock?: string;
    sectionTitle?: string;
    autoCreateLink?: boolean;
    promptShareUrl?: string;
  } | null>(null);

  const [projectSwitcherOpen, setProjectSwitcherOpen] = createSignal(false);
  const [projectSwitcherInitialView, setProjectSwitcherInitialView] = createSignal<"grid" | "form">("grid");
  const [preOpenProjectId, setPreOpenProjectId] = createSignal<string | undefined>();

  const handleProjectSwitcherChange = (open: boolean) => {
    if (open) {
      setPreOpenProjectId(wire.userContext.get()?.projectId);
    } else {
      const currentProjectId = wire.userContext.get()?.projectId;
      if (preOpenProjectId() && currentProjectId !== preOpenProjectId()) {
        nav.navigateHome();
        if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
      }
      setProjectSwitcherInitialView("grid");
    }
    setProjectSwitcherOpen(open);
  };

  const doCreateProject = () => {
    setProjectSwitcherInitialView("form");
    handleProjectSwitcherChange(true);
  };
  const handleCreateProject = useGateActionByAuthenticated(
    "createProject",
    () => ({
      title: t("modal_signup_create_projects_title"),
      description: t("modal_signup_create_projects_desc"),
      actionType: "project",
    }),
    doCreateProject
  );

  const handleDeleteChat = async () => {
    const chat = deletingChat();
    if (!chat || isDeletingChat()) return;
    // Guard against late completions painting an outcome onto a different chat's modal.
    const isSameModalSession = () => deletingChat()?.chatId === chat.chatId;
    setDeletingChatError(null);
    setIsDeletingChat(true);
    try {
      await doDeleteChat(chat, wire);
      if (isSameModalSession()) closeDeleteChatModal();
    } catch (err) {
      console.error("Failed to delete chat:", err);
      if (!isSameModalSession()) return;
      setDeletingChatError(extractErrorInfo(err).message ?? t("delete_chat_failed"));
      setIsDeletingChat(false);
    }
  };

  // Use recent chats data from wire service (stable across component remounts)
  const recentChats = () => wire.recentChats.chats();

  /** Capped server-side at SIDEBAR_RECENT_CHATS_CAP. */
  const displayedRecentChats = createMemo(() => {
    const list = recentChats();
    if (!list) return [];
    const me = wire.auth.user()?.id;
    const myEmail = wire.auth.user()?.primaryEmail;
    if (authorFilter() === "mine" && (me || myEmail)) return list.filter((c) => isChatMine(c, me, myEmail));
    return list;
  });

  /** Subscribe to stream status of all recent chats so spinner updates when any chat finishes (even if we're on another page) */
  const recentChatsStreamingKeys = createMemo(() => {
    const list = displayedRecentChats();
    return list.map((c) => `${c.chatId}:${wire.stream.chat.getStatus(c.chatId as StreamingChatId)}`).join(",");
  });

  // Note: Refetch event subscriptions are handled in useRecentChats hook (parent level)
  // to avoid recreating them when this component remounts due to collapsed/expanded toggles
  onMount(() => {
    const unsubscribeOpenDeleteModal = subscribeToGlobalEvent("open-delete-chat-modal", (event) => {
      const chatId = event.detail.chatId;
      const title = wire.chatTitle.getChatTitle(chatId) ?? "";
      setDeletingChat({ chatId, title });
    });
    const unsubscribeOpenMoveModal = subscribeToGlobalEvent("open-move-chat-modal", (event) => {
      if (!nav.state().projectId) return;
      setMovingChatIds([event.detail.chatId]);
    });
    const unsubscribeOpenShareModal = subscribeToGlobalEvent("open-share-chat-modal", (event) => {
      const chatId = event.detail.chatId;
      const title = wire.chatTitle.getChatTitle(chatId) ?? "";
      // Whole-page share: surface the chat title in the "Share text block" section without affecting the URL.
      setSharingChat({ chatId, title, textBlock: title });
    });
    const unsubscribeOpenShareModalWithFragment = subscribeToGlobalEvent(
      "open-share-chat-modal-with-fragment",
      (event) => {
        const { chatId, textFragment, scrollToText, textBlock, sectionTitle, scrollToMessageId, promptShareUrl } =
          event.detail;
        const title = wire.chatTitle.getChatTitle(chatId) ?? "";
        setSharingChat({
          chatId,
          title,
          textFragment,
          scrollToText,
          textBlock,
          sectionTitle,
          scrollToMessageId,
          autoCreateLink: true,
          promptShareUrl,
        });
      }
    );
    const unsubscribeOpenProjectSwitcher = subscribeToGlobalEvent("open-project-switcher", () => {
      handleProjectSwitcherChange(true);
    });

    onCleanup(() => {
      unsubscribeOpenDeleteModal();
      unsubscribeOpenMoveModal();
      unsubscribeOpenShareModal();
      unsubscribeOpenShareModalWithFragment();
      unsubscribeOpenProjectSwitcher();
    });
  });

  return (
    <>
      <div class="mt-1 flex flex-col gap-1 pb-1">
        <ProjectsNavSectionExpanded
          onOpenSwitcher={() => handleProjectSwitcherChange(true)}
          onCreateProject={handleCreateProject}
        />
      </div>

      <div class="border-t border-primary-border pb-1" aria-hidden />

      <div class="group/new-chat relative">
        <StButton
          as={A}
          href={homeHref()}
          onClick={(e: MouseEvent) => {
            if (e.metaKey || e.ctrlKey) return;
            e.preventDefault();
            const currentHome = homeHref();
            const startNewTask = () => {
              if (typeof window !== "undefined" && window.location.pathname === currentHome && window.location.search) {
                window.history.replaceState({}, "", currentHome);
              }
              runStartNewTask(wire);
              if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
            };
            if (guardUnsavedProjectDetails(startNewTask)) return;
            startNewTask();
          }}
          icon={TbPlus}
          iconClass="text-on-brand"
          noBorder
          noColor
          class="min-h-8 w-full justify-start bg-brand py-2 pl-4 text-sm whitespace-nowrap text-on-brand hover:bg-brand-hover"
        >
          {t("nav_new")}
        </StButton>
        <KeyboardShortcutInlinePill
          keys={["Opt", "Shift", "N"]}
          alwaysVisible
          class="absolute top-1/2 right-4 -translate-y-1/2 opacity-0 transition-opacity duration-150 group-hover/new-chat:opacity-100"
        />
      </div>

      {/* No gap between rows: the hover and active fills are the row, so a gap here
          would paint them as pills floating in a taller band. */}
      <div class="flex flex-col pb-1">
        <NavLink page="chats" icon={TbListCheck} label={t("nav_chats")} />
        <LibraryNavSectionExpanded />
        <Show when={isUserModeAdvanced()}>
          <NavLink page="concepts" icon={TbApps} label={t("entity.concepts")} />
        </Show>
        <NavLink page="skills" icon={FaSolidGraduationCap} label={t("skills.title")} />
        <NavLink page="personas" icon={RiUserFacesUserStarLine} label={t("personas.page_title")} />
        <NavLink page="actions" icon={TbBolt} label={t("actions.title")} />
        <LiveSessionsNavSectionExpanded />
      </div>

      <div class="border-t border-primary-border pb-1" aria-hidden />

      <Suspense>
        {/* This block carries the flex-1 that pushes the footer to the bottom of the sidebar, so it
            stays mounted when there is nothing to list and only its contents are gated.
            Gated on the unfiltered list: keying it on the filtered one would pull the All/Mine
            toggle out from under the user the moment they switched to an empty scope. */}
        <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Show when={(recentChats()?.length ?? 0) > 0}>
            <div class="mb-1 flex items-center justify-between gap-2">
              <h2 class={SIDEBAR_GROUP_LABEL}>{t("nav_recent_chats")}</h2>
              <RecentChatsFilterMenu
                value={authorFilter}
                onChange={(value) => {
                  trackFeature({ feature: "nav.recent_chats_filter", via: "menu" });
                  setAuthorFilter(value);
                }}
              />
            </div>

            {/* recentChatsStreamingKeys() ensures we re-render when any chat's stream status changes.
              Capped server-side at SIDEBAR_RECENT_CHATS_CAP (50); a "More..." row routes to /chats when the cap is hit. */}
            <div class="min-h-0 flex-1 overflow-y-auto" data-streaming-keys={recentChatsStreamingKeys()}>
              <For each={groupRecentChats(displayedRecentChats())}>
                {(group) => (
                  <>
                    <h3 class={RECENT_DATE_LABEL}>{t(group.labelKey)}</h3>
                    <For each={group.chats}>
                      {(item) => {
                        const chatHref = () => {
                          const projectId = wire.currentProjectId();
                          if (!projectId) return "#";
                          return nav.buildUrl({ projectId, primaryId: item.chatId });
                        };
                        const isActive = () => nav.state().primaryId === item.chatId;
                        const handleClick = (e: MouseEvent) => {
                          if (e.metaKey || e.ctrlKey) {
                            e.preventDefault();
                            const href = chatHref();
                            if (href && href !== "#") window.open(href, "_blank", "noopener");
                            return;
                          }
                          e.preventDefault();
                          nav.navigateToPrimary(item.chatId, { preserveSidePanel: true });
                          if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
                        };

                        const timestampLabel = () => {
                          const ts = getDisplayTimestamp(item);
                          return ts ? recentChatTimestamp(group.id, ts) : "";
                        };

                        return (
                          <StTooltip
                            placement="right"
                            openDelay={300}
                            content={
                              <div class="max-w-70 min-w-50 p-2">
                                <span class="mb-2 block text-sm wrap-break-word text-primary-text">
                                  {item.title || t("nav_new_chat")}
                                </span>
                                {(() => {
                                  const { createdBy, modifiedBy, createdDate, modifiedDate } = getChatAuditLines(
                                    item,
                                    t,
                                    wire.auth.user()?.id
                                  );
                                  return (
                                    <div class="flex flex-col gap-0.5 text-xs text-muted-text">
                                      <Show when={createdDate}>
                                        {(date) => (
                                          <p>{t("chats_first_created_by_on", { name: createdBy, date: date() })}</p>
                                        )}
                                      </Show>
                                      <Show when={modifiedDate}>
                                        {(date) => (
                                          <p>{t("chats_last_chat_by_on", { name: modifiedBy, date: date() })}</p>
                                        )}
                                      </Show>
                                    </div>
                                  );
                                })()}
                              </div>
                            }
                          >
                            <StTooltipTrigger as="div" class="w-full">
                              <A
                                href={chatHref()}
                                onClick={handleClick}
                                {...prefetchChatOnIntent(item.chatId, wire.chat.prefetchChats)}
                                class="flex w-full items-center gap-2 rounded-md px-4 py-1.5 text-sm transition-colors"
                                classList={{
                                  "bg-panel-hover text-primary-text font-medium": isActive(),
                                  "hover:bg-canvas": !isActive(),
                                }}
                              >
                                <Show
                                  when={
                                    wire.stream.chat.getStatus(item.chatId as StreamingChatId) ===
                                    StreamingChatStatus.GENERATING
                                  }
                                >
                                  <TbLoader2 class="size-4 shrink-0 animate-spin text-muted-text" aria-hidden />
                                </Show>
                                <span class="min-w-0 flex-1 truncate">{item.title}</span>
                                <Show when={getDisplayTimestamp(item)}>
                                  <span class="shrink-0 text-xs text-muted-text tabular-nums">{timestampLabel()}</span>
                                </Show>
                              </A>
                            </StTooltipTrigger>
                          </StTooltip>
                        );
                      }}
                    </For>
                  </>
                )}
              </For>
              <Show when={wire.recentChats.hitCap()}>
                <A
                  href="/chats"
                  class="block rounded-md px-4 py-1.5 text-sm text-secondary-text transition-colors hover:bg-canvas hover:text-primary-text"
                >
                  {t("nav_recent_chats_more")}
                </A>
              </Show>
            </div>
          </Show>
        </div>
      </Suspense>

      <DeleteChatModal
        isOpen={deletingChat() !== null}
        onClose={closeDeleteChatModal}
        onConfirm={() => void handleDeleteChat()}
        chatTitle={deletingChat()?.title ?? ""}
        isShared={false}
        errorMessage={deletingChatError()}
        isDeleting={isDeletingChat()}
        isStreaming={(() => {
          const chat = deletingChat();
          return chat ? wire.chat.getChatId() === chat.chatId && wire.chat.isChatStreaming() : false;
        })()}
      />

      <Show when={nav.state().projectId}>
        {(currentProjectId) => (
          <MoveChatModal
            open={movingChatIds().length > 0}
            onOpenChange={(open) => {
              if (!open) setMovingChatIds([]);
            }}
            selectedChatIds={movingChatIds()}
            currentProjectId={currentProjectId() as ProjectId}
          />
        )}
      </Show>

      <ShareChatModal
        isOpen={sharingChat() !== null}
        onClose={() => setSharingChat(null)}
        chatId={sharingChat()?.chatId ?? ""}
        chatTitle={sharingChat()?.title ?? ""}
        textFragment={sharingChat()?.textFragment}
        scrollToText={sharingChat()?.scrollToText}
        scrollToMessageId={sharingChat()?.scrollToMessageId}
        textBlock={sharingChat()?.textBlock}
        sectionTitle={sharingChat()?.sectionTitle}
        autoCreateLink={sharingChat()?.autoCreateLink}
        promptShareUrl={sharingChat()?.promptShareUrl}
      />

      <ProjectSwitcherModal
        open={projectSwitcherOpen()}
        onOpenChange={handleProjectSwitcherChange}
        initialView={projectSwitcherInitialView()}
      />
    </>
  );
};

/** Update prompt, the hover-revealed utility icons, and the account row. Shared by both content modes. */
const SidebarFooterExpanded = () => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const { t } = useI18n();
  const [orgSwitcherOpen, setOrgSwitcherOpen] = createSignal(false);

  return (
    <div class="shrink-0">
      <Show when={updateAvailable()}>
        <button
          type="button"
          class="relative mb-2 flex w-full items-center justify-center gap-2 rounded-md border border-emerald-500/50 bg-panel px-4 py-2 text-sm font-medium text-primary-text shadow-[0_0_8px_--alpha(var(--color-glow-emerald)/20%)] transition-colors duration-200 hover:bg-canvas"
          onClick={reloadForUpdate}
        >
          <span class="absolute top-1.5 right-1.5 flex size-2">
            <span class="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-75" />
            <span class="relative inline-flex size-2 rounded-full bg-green-500" />
          </span>
          <TbRefresh class="size-4" />
          {t("nav_update_available")}
        </button>
      </Show>

      <div class="mb-2 flex items-center justify-center gap-1 border-t border-primary-border pt-2">
        <WhatsNewPopover
          placement="top"
          buttonClass="p-2 hover:bg-canvas transition-colors rounded-md flex items-center justify-center"
        />

        <StTooltip content={t("nav_get_help")} placement="top">
          <StTooltipTrigger
            as="a"
            href={SUPPORT_MAILTO}
            class="flex items-center justify-center rounded-md p-2 transition-colors hover:bg-canvas"
            aria-label={t("nav_get_help")}
            onClick={() => trackFeature({ feature: "nav.help", via: "button" })}
          >
            <TbHelp class="size-5 text-secondary-text" />
          </StTooltipTrigger>
        </StTooltip>

        <ThemeToggle placement="top" />

        <NotificationBell />
      </div>

      <Switch>
        <Match when={!wire.auth?.isReady()}>
          <div class="flex items-center justify-center py-2">
            <TbLoader2 class="size-6 animate-spin text-muted-text" />
          </div>
        </Match>
        <Match when={!wire.auth?.isRegistered()}>
          <StDropdown
            modal={false}
            placement="top-end"
            gutter={5}
            open={layout.userMenuDropdownOpen()}
            onOpenChange={layout.setUserMenuDropdownOpen}
            contentProps={{
              class: "z-50",
            }}
            content={<UserAccountPopover onOpenOrganizations={() => setOrgSwitcherOpen(true)} />}
          >
            <StDropdownTrigger
              as="button"
              type="button"
              class="flex w-full cursor-pointer items-center gap-3 rounded-lg px-4 py-2 text-left transition-colors hover:bg-canvas"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              <div class="shrink-0">
                <Avatar src={wire.auth?.user()?.avatarUrl} name={wire.auth?.user()?.displayName} size="md" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium text-primary-text">{t("nav_guest")}</div>
              </div>
            </StDropdownTrigger>
          </StDropdown>
        </Match>
        <Match when={true}>
          <StDropdown
            modal={false}
            placement="top"
            gutter={5}
            open={layout.userMenuDropdownOpen()}
            onOpenChange={layout.setUserMenuDropdownOpen}
            contentProps={{
              class: "z-50 w-[var(--kb-popper-anchor-width)]",
            }}
            content={<UserAccountPopover onOpenOrganizations={() => setOrgSwitcherOpen(true)} />}
          >
            <StDropdownTrigger
              as="div"
              class="flex cursor-pointer items-center gap-3 rounded-lg px-4 py-2 transition-colors hover:bg-canvas"
            >
              <div class="shrink-0">
                <Avatar src={wire.auth?.user()?.avatarUrl} name={wire.auth?.user()?.displayName} size="md" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium text-primary-text">
                  {wire.auth?.user()?.displayName || t("common.you")}
                </div>
                <Suspense>
                  <div class="truncate text-xs text-muted-text">{wire.billing.planDisplayName()}</div>
                </Suspense>
              </div>
              <div class="flex shrink-0 gap-1">
                <button type="button" class="rounded p-1 transition-colors hover:bg-canvas">
                  <TbSettings class="size-5" />
                </button>
              </div>
            </StDropdownTrigger>
          </StDropdown>
        </Match>
      </Switch>

      <OrganizationSwitcherModal open={orgSwitcherOpen()} onOpenChange={setOrgSwitcherOpen} />
    </div>
  );
};

const SidebarFooterCollapsed = () => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const { t } = useI18n();
  const [orgSwitcherOpen, setOrgSwitcherOpen] = createSignal(false);

  return (
    <div class="shrink-0 space-y-2">
      <Show when={updateAvailable()}>
        <StTooltip placement="right" content={<div>{t("nav_update_available")}</div>}>
          <StTooltipTrigger
            as="button"
            type="button"
            class="relative flex cursor-pointer items-center justify-center rounded-md p-2 transition-all duration-150 hover:bg-canvas focus:outline-none"
            aria-label={t("nav_update_refresh")}
            onClick={reloadForUpdate}
          >
            <span class="absolute top-1 right-1 flex size-2">
              <span class="absolute inline-flex size-full animate-ping rounded-full bg-green-400 opacity-75" />
              <span class="relative inline-flex size-2 rounded-full bg-green-500" />
            </span>
            <TbRefresh class="size-5 text-primary-text" />
          </StTooltipTrigger>
        </StTooltip>
      </Show>

      <div class="flex flex-col space-y-2">
        <div class="flex flex-col space-y-2">
          <WhatsNewPopover
            placement="right"
            buttonClass="p-2 hover:bg-canvas transition-all duration-150 focus:outline-none rounded-md cursor-pointer flex items-center justify-center"
          />

          <StTooltip placement="right" content={<div>{t("nav_get_help")}</div>}>
            <StTooltipTrigger
              as="a"
              href={SUPPORT_MAILTO}
              class="flex cursor-pointer items-center justify-center rounded-md p-2 transition-all duration-150 hover:bg-canvas focus:outline-none"
              aria-label={t("nav_get_help")}
              onClick={() => trackFeature({ feature: "nav.help", via: "button" })}
            >
              <TbHelp class="size-5 text-secondary-text" />
            </StTooltipTrigger>
          </StTooltip>

          <div class="flex justify-center">
            <ThemeToggle placement="right" />
          </div>

          <div class="flex justify-center">
            <NotificationBell />
          </div>
        </div>

        <Switch>
          <Match when={!wire.auth?.isReady()}>
            <div class="flex items-center justify-center p-2">
              <TbLoader2 class="size-6 animate-spin text-muted-text" />
            </div>
          </Match>
          <Match when={!wire.auth?.isRegistered()}>
            <StDropdown
              modal={false}
              placement="right-start"
              gutter={8}
              open={layout.userMenuDropdownOpen()}
              onOpenChange={layout.setUserMenuDropdownOpen}
              contentProps={{
                class: "z-50",
              }}
              content={<UserAccountPopover onOpenOrganizations={() => setOrgSwitcherOpen(true)} />}
            >
              <StTooltip content={t("tooltip_user_menu")} placement="right">
                <StDropdownTrigger
                  as="button"
                  type="button"
                  class="flex cursor-pointer items-center justify-center rounded-md p-2 transition-colors hover:bg-canvas"
                  aria-label={t("tooltip_user_menu")}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <svg class="size-6 text-muted-text" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </StDropdownTrigger>
              </StTooltip>
            </StDropdown>
          </Match>
          <Match when={true}>
            <StDropdown
              modal={false}
              placement="right-start"
              gutter={8}
              open={layout.userMenuDropdownOpen()}
              onOpenChange={layout.setUserMenuDropdownOpen}
              contentProps={{
                class: "z-50",
              }}
              content={<UserAccountPopover onOpenOrganizations={() => setOrgSwitcherOpen(true)} />}
            >
              <StTooltip content={wire.auth?.user()?.displayName || t("tooltip_user_menu")} placement="right">
                <StDropdownTrigger
                  as="button"
                  type="button"
                  class="flex cursor-pointer items-center justify-center rounded-md p-1 transition-colors hover:bg-canvas"
                  aria-label={t("tooltip_user_menu")}
                >
                  <Avatar src={wire.auth?.user()?.avatarUrl} name={wire.auth?.user()?.displayName} size="sm" />
                </StDropdownTrigger>
              </StTooltip>
            </StDropdown>
          </Match>
        </Switch>
      </div>

      <OrganizationSwitcherModal open={orgSwitcherOpen()} onOpenChange={setOrgSwitcherOpen} />
    </div>
  );
};

const MainNavCollapsed = () => {
  const wire = useWire();
  const layout = wire.ui.layout;
  const nav = wire.navigation;
  const { t } = useI18n();
  const { isAdvanced: isUserModeAdvanced } = useUserMode();
  const logoHref = useHomeHref();
  const searchHref = useSearchHref();

  const [highlightAdvancedNavIcons, setHighlightAdvancedNavIcons] = createSignal(false);
  const [projectSwitcherOpen, setProjectSwitcherOpen] = createSignal(false);
  const activeProjectName = createMemo(() => {
    const projectId = wire.userContext.get()?.projectId;
    if (!projectId) return undefined;
    return wire.projects.get(projectId)?.name;
  });
  const [deletingChat, setDeletingChat] = createSignal<{ chatId: string; title: string } | null>(null);
  const [movingChatIds, setMovingChatIds] = createSignal<StreamingChatId[]>([]);
  const [deletingChatError, setDeletingChatError] = createSignal<string | null>(null);
  const [isDeletingChat, setIsDeletingChat] = createSignal(false);
  const closeDeleteChatModal = () => {
    setDeletingChat(null);
    setDeletingChatError(null);
    setIsDeletingChat(false);
  };
  const [sharingChat, setSharingChat] = createSignal<{
    chatId: string;
    title: string;
    textFragment?: string;
    scrollToText?: string;
    scrollToMessageId?: string;
    textBlock?: string;
    sectionTitle?: string;
    autoCreateLink?: boolean;
    promptShareUrl?: string;
  } | null>(null);

  const handleDeleteChat = async () => {
    const chat = deletingChat();
    if (!chat || isDeletingChat()) return;
    // Guard against late completions painting an outcome onto a different chat's modal.
    const isSameModalSession = () => deletingChat()?.chatId === chat.chatId;
    setDeletingChatError(null);
    setIsDeletingChat(true);
    try {
      await doDeleteChat(chat, wire);
      if (isSameModalSession()) closeDeleteChatModal();
    } catch (err) {
      console.error("Failed to delete chat:", err);
      if (!isSameModalSession()) return;
      setDeletingChatError(extractErrorInfo(err).message ?? t("delete_chat_failed"));
      setIsDeletingChat(false);
    }
  };

  onMount(() => {
    let highlightTimeout: ReturnType<typeof setTimeout> | undefined;
    const runHighlight = () => {
      if (highlightTimeout) clearTimeout(highlightTimeout);
      setHighlightAdvancedNavIcons(true);
      highlightTimeout = setTimeout(() => setHighlightAdvancedNavIcons(false), 2500);
    };
    if (consumePendingHighlight("nav-icons")) runHighlight();

    const unsubscribeOpenDeleteModal = subscribeToGlobalEvent("open-delete-chat-modal", (event) => {
      const chatId = event.detail.chatId;
      const title = wire.chatTitle.getChatTitle(chatId) ?? "";
      setDeletingChat({ chatId, title });
    });
    const unsubscribeOpenMoveModal = subscribeToGlobalEvent("open-move-chat-modal", (event) => {
      if (!nav.state().projectId) return;
      setMovingChatIds([event.detail.chatId]);
    });
    const unsubscribeOpenShareModal = subscribeToGlobalEvent("open-share-chat-modal", (event) => {
      const chatId = event.detail.chatId;
      const title = wire.chatTitle.getChatTitle(chatId) ?? "";
      // Whole-page share: surface the chat title in the "Share text block" section without affecting the URL.
      setSharingChat({ chatId, title, textBlock: title });
    });
    const unsubscribeOpenShareModalWithFragment = subscribeToGlobalEvent(
      "open-share-chat-modal-with-fragment",
      (event) => {
        const { chatId, textFragment, scrollToText, textBlock, sectionTitle, scrollToMessageId, promptShareUrl } =
          event.detail;
        const title = wire.chatTitle.getChatTitle(chatId) ?? "";
        setSharingChat({
          chatId,
          title,
          textFragment,
          scrollToText,
          textBlock,
          sectionTitle,
          scrollToMessageId,
          autoCreateLink: true,
          promptShareUrl,
        });
      }
    );
    const unsubscribeOpenProjectSwitcher = subscribeToGlobalEvent("open-project-switcher", () => {
      setProjectSwitcherOpen(true);
    });

    onCleanup(() => {
      if (highlightTimeout) clearTimeout(highlightTimeout);
      unsubscribeOpenDeleteModal();
      unsubscribeOpenMoveModal();
      unsubscribeOpenShareModal();
      unsubscribeOpenShareModalWithFragment();
      unsubscribeOpenProjectSwitcher();
    });
  });

  return (
    <>
      <StTooltip content={t("chats.search.input.placeholder")} placement="right">
        <StTooltipTrigger as="div" class="w-full">
          <StButton
            type="button"
            as={A}
            href={searchHref()}
            onClick={() => {
              if (layout.isSmallScreen()) layout.setNavbarCollapsed(true);
            }}
            icon={TbSearch}
            iconClass="text-muted-text"
            noBorder
            noColor
            class="min-h-8 w-full justify-start py-1 pl-2 transition-colors hover:bg-canvas [&_svg]:size-4"
            aria-label={t("chats.search.input.aria_label")}
          />
        </StTooltipTrigger>
      </StTooltip>

      {/* With no breadcrumb left, the rail would otherwise name the project nowhere. */}
      <StTooltip content={activeProjectName() ?? t("nav_select_project")} placement="right">
        <StTooltipTrigger as="div" class="w-full">
          <button
            type="button"
            onClick={() => setProjectSwitcherOpen(true)}
            aria-label={activeProjectName() ?? t("nav_select_project")}
            class="flex min-h-8 w-full cursor-pointer items-center justify-center rounded-md px-2 py-1 transition-colors hover:bg-canvas"
          >
            {/* Inverted against the rail so two letters stay legible at 20px: both tokens
                flip with the theme, so this is white-on-black in dark and the reverse in light. */}
            <span class="flex size-5 shrink-0 items-center justify-center rounded bg-primary-text text-[0.625rem] leading-none font-semibold text-panel uppercase">
              {projectInitials(activeProjectName())}
            </span>
          </button>
        </StTooltipTrigger>
      </StTooltip>

      <div>
        <StTooltip
          content={
            <span class="flex items-center gap-2">
              {t("nav_new")}
              <KeyboardShortcutInlinePill keys={["Opt", "Shift", "N"]} alwaysVisible />
            </span>
          }
          placement="right"
        >
          <StTooltipTrigger as="div" class="w-full">
            <StButton
              as={A}
              href={logoHref()}
              aria-label={t("nav_new")}
              onClick={(e: MouseEvent) => {
                if (e.metaKey || e.ctrlKey) return;
                e.preventDefault();
                const currentHome = logoHref();
                const startNewTask = () => {
                  if (
                    typeof window !== "undefined" &&
                    window.location.pathname === currentHome &&
                    window.location.search
                  ) {
                    window.history.replaceState({}, "", currentHome);
                  }
                  runStartNewTask(wire);
                };
                if (guardUnsavedProjectDetails(startNewTask)) return;
                startNewTask();
              }}
              icon={TbPlus}
              iconClass="text-on-brand"
              noBorder
              noColor
              class="min-h-8 w-full justify-start bg-brand py-2 pl-2 text-sm text-on-brand hover:bg-brand-hover [&_svg]:size-4"
            />
          </StTooltipTrigger>
        </StTooltip>
      </div>

      <NavLinkIcon page="chats" icon={TbListCheck} tooltip={t("nav_chats")} />

      <LibraryNavSectionCollapsed collectionsHighlight={() => highlightAdvancedNavIcons()} />
      <Show when={isUserModeAdvanced()}>
        <NavLinkIcon
          page="concepts"
          icon={TbApps}
          tooltip={t("entity.concepts")}
          highlight={() => highlightAdvancedNavIcons()}
        />
      </Show>
      <NavLinkIcon page="skills" icon={FaSolidGraduationCap} tooltip={t("skills.title")} />
      <NavLinkIcon page="personas" icon={RiUserFacesUserStarLine} tooltip={t("personas.page_title")} />
      <NavLinkIcon page="actions" icon={TbBolt} tooltip={t("actions.title")} />
      <NavLinkIcon page="live-sessions" icon={TbWaveSquare} tooltip={t("nav_live_sessions")} />

      <div class="my-1 border-t border-primary-border" aria-hidden />
      <RecentChatsNavSectionCollapsed />

      <div class="flex-auto" aria-hidden />

      <ProjectSwitcherModal open={projectSwitcherOpen()} onOpenChange={setProjectSwitcherOpen} />

      <DeleteChatModal
        isOpen={deletingChat() !== null}
        onClose={closeDeleteChatModal}
        onConfirm={() => void handleDeleteChat()}
        chatTitle={deletingChat()?.title ?? ""}
        isShared={false}
        errorMessage={deletingChatError()}
        isDeleting={isDeletingChat()}
        isStreaming={(() => {
          const chat = deletingChat();
          return chat ? wire.chat.getChatId() === chat.chatId && wire.chat.isChatStreaming() : false;
        })()}
      />

      <Show when={nav.state().projectId}>
        {(currentProjectId) => (
          <MoveChatModal
            open={movingChatIds().length > 0}
            onOpenChange={(open) => {
              if (!open) setMovingChatIds([]);
            }}
            selectedChatIds={movingChatIds()}
            currentProjectId={currentProjectId() as ProjectId}
          />
        )}
      </Show>

      <ShareChatModal
        isOpen={sharingChat() !== null}
        onClose={() => setSharingChat(null)}
        chatId={sharingChat()?.chatId ?? ""}
        chatTitle={sharingChat()?.title ?? ""}
        textFragment={sharingChat()?.textFragment}
        scrollToText={sharingChat()?.scrollToText}
        scrollToMessageId={sharingChat()?.scrollToMessageId}
        textBlock={sharingChat()?.textBlock}
        sectionTitle={sharingChat()?.sectionTitle}
        autoCreateLink={sharingChat()?.autoCreateLink}
        promptShareUrl={sharingChat()?.promptShareUrl}
      />
    </>
  );
};
